// App.js
// Location: src/App.js
// Main application component - manages all state and screens

import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import LibraryPage from './pages/LibraryPage';
import SongsPage from './pages/SongsPage';
import SessionFlow from './components/SessionFlow';
import { useRoutineGenerator } from './hooks/useRoutineGenerator';
import { songProgression } from './data/DrumData.v2';

function App() {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  // Screen navigation
  const [currentScreen, setCurrentScreen] = useState('home');
  
  // Session state
  const [activeSession, setActiveSession] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  
  // Songs state (loaded from localStorage or default data)
  const [songs, setSongs] = useState(() => {
    const saved = localStorage.getItem('drumflow_songs');
    return saved ? JSON.parse(saved) : songProgression;
  });
  
  // User stats (loaded from localStorage or defaults)
  const [stats, setStats] = useState(() => {
    const saved = localStorage.getItem('drumflow_stats');
    return saved ? JSON.parse(saved) : {
      streak: 0,
      sessions: 0,
      lastPracticeDate: null
    };
  });
  
  // Pin/Exclude state for lessons
  const [pinnedItems, setPinnedItems] = useState(() => {
    const saved = localStorage.getItem('drumflow_pinned');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [excludedItems, setExcludedItems] = useState(() => {
    const saved = localStorage.getItem('drumflow_excluded');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Routine version to force re-renders
  const [routineVersion, setRoutineVersion] = useState(0);
  
  // Custom hook for routine generation
  const { routine, generateRoutine } = useRoutineGenerator(
    songs,
    pinnedItems,
    excludedItems
  );
  
  // ============================================================================
  // PERSIST STATE TO LOCALSTORAGE
  // ============================================================================
  
  useEffect(() => {
    localStorage.setItem('drumflow_songs', JSON.stringify(songs));
  }, [songs]);
  
  useEffect(() => {
    localStorage.setItem('drumflow_stats', JSON.stringify(stats));
  }, [stats]);
  
  useEffect(() => {
    localStorage.setItem('drumflow_pinned', JSON.stringify(pinnedItems));
  }, [pinnedItems]);
  
  useEffect(() => {
    localStorage.setItem('drumflow_excluded', JSON.stringify(excludedItems));
  }, [excludedItems]);
  
  // ============================================================================
  // ROUTINE GENERATION
  // ============================================================================
  
  const handleGenerateRoutine = () => {
    console.log('🔄 App.js: Regenerate routine triggered');
    generateRoutine();
    setRoutineVersion(prev => prev + 1); // Force re-render
  };
  
  // ============================================================================
  // SESSION MANAGEMENT
  // ============================================================================
  
  const handleStartSession = (sessionType) => {
    setActiveSession(sessionType);
    setCurrentStep(0);
    setCurrentScreen('session');
  };
  
  const handleSessionNext = () => {
    const totalSteps = activeSession === '20min' ? 3 : 2;
    
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Complete session
      handleCompleteSession();
    }
  };
  
  const handleCompleteSession = () => {
    // Update stats
    const today = new Date().toDateString();
    const lastDate = stats.lastPracticeDate;
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    
    let newStreak = stats.streak;
    if (lastDate === yesterday) {
      newStreak += 1;
    } else if (lastDate !== today) {
      newStreak = 1;
    }
    
    setStats({
      streak: newStreak,
      sessions: stats.sessions + 1,
      lastPracticeDate: today
    });
    
    // Return to home
    setActiveSession(null);
    setCurrentStep(0);
    setCurrentScreen('home');
  };
  
  const handleExitSession = () => {
    setActiveSession(null);
    setCurrentStep(0);
    setCurrentScreen('home');
  };
  
  // ============================================================================
  // SONG MANAGEMENT
  // ============================================================================
  
  const handleMarkComplete = (songId) => {
    const updatedSongs = songs.map(song => {
      if (song.id === songId) {
        return { ...song, status: 'completed' };
      }
      return song;
    });
    
    // Find next song to set as current
    const completedSongIndex = songs.findIndex(s => s.id === songId);
    const nextSong = songs.find((s, index) => 
      index > completedSongIndex && 
      s.status === 'not-started' && 
      !s.hidden
    );
    
    if (nextSong) {
      updatedSongs.forEach(song => {
        if (song.id === nextSong.id) {
          song.status = 'current';
        }
      });
    }
    
    setSongs(updatedSongs);
  };
  
  const handleSetCurrent = (songId) => {
    const updatedSongs = songs.map(song => {
      if (song.id === songId) {
        return { ...song, status: 'current' };
      }
      // Remove current status from other songs (only one can be current)
      if (song.status === 'current') {
        return { ...song, status: 'not-started' };
      }
      return song;
    });
    
    setSongs(updatedSongs);
  };
  
  const handleToggleSongHidden = (songId) => {
    const updatedSongs = songs.map(song => {
      if (song.id === songId) {
        return { ...song, hidden: !song.hidden };
      }
      return song;
    });
    
    setSongs(updatedSongs);
  };
  
  // ============================================================================
  // LESSON PIN/EXCLUDE MANAGEMENT
  // ============================================================================
  
  const handleTogglePin = (itemId) => {
    setPinnedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
    
    // Remove from excluded if pinning
    if (!pinnedItems.includes(itemId)) {
      setExcludedItems(prev => prev.filter(id => id !== itemId));
    }
  };
  
  const handleToggleExclude = (itemId) => {
    setExcludedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
    
    // Remove from pinned if excluding
    if (!excludedItems.includes(itemId)) {
      setPinnedItems(prev => prev.filter(id => id !== itemId));
    }
  };
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* Header (hidden during active session) */}
      {!activeSession && (
        <Header 
          currentScreen={currentScreen}
          onNavigate={setCurrentScreen}
          stats={stats}
          songs={songs}
        />
      )}
      
      {/* Main Content */}
      <main>
        {/* Home Screen */}
        {currentScreen === 'home' && !activeSession && (
          <HomePage 
            routine={routine}
            onGenerateRoutine={handleGenerateRoutine}
            onStartSession={handleStartSession}
            songs={songs}
            onMarkComplete={handleMarkComplete}
            routineVersion={routineVersion}
          />
        )}
        
        {/* Library Screen */}
        {currentScreen === 'library' && (
          <LibraryPage 
            onBack={() => setCurrentScreen('home')}
            pinnedItems={pinnedItems}
            excludedItems={excludedItems}
            onTogglePin={handleTogglePin}
            onToggleExclude={handleToggleExclude}
          />
        )}
        
        {/* Songs Screen */}
        {currentScreen === 'songs' && (
          <SongsPage 
            onBack={() => setCurrentScreen('home')}
            songs={songs}
            onMarkComplete={handleMarkComplete}
            onToggleHidden={handleToggleSongHidden}
            onSetCurrent={handleSetCurrent}
          />
        )}
        
        {/* Session Flow Screen */}
        {activeSession && (
          <SessionFlow 
            sessionType={activeSession}
            currentStep={currentStep}
            routine={routine}
            onNext={handleSessionNext}
            onExit={handleExitSession}
          />
        )}
      </main>
      
      {/* Footer (hidden during active session) */}
      {!activeSession && <Footer />}
    </div>
  );
}

export default App;